import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../hooks/useAuth";
import CountryFlag from "../../components/CountryFlag";
import { FaShareAlt, FaPencilAlt, FaHeart, FaEye } from "react-icons/fa";
import { jsPDF } from "jspdf";

/** ---- UI helpers ---- */
function Chip({ children }) {
  return (
    <span className="inline-flex max-w-full items-center rounded-full border border-dulia-line bg-white/90 px-3 py-1 text-left text-xs font-medium text-dulia-ink dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment">
      {children}
    </span>
  );
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function trimText(s, maxChars) {
  const t = String(s ?? "").trim();
  if (!t) return "";
  if (t.length <= maxChars) return t;

  const cut = t.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > maxChars * 0.6 ? cut.slice(0, lastSpace) : cut;
  return safe.trim() + "…";
}

function formatContributorList(items) {
  const arr = Array.isArray(items) ? items.filter(Boolean) : [];

  if (!arr.length) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;

  return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
}

function renderChips(items) {
  const arr = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!arr.length) return "";
  return arr.map((x) => `<span class="chip">${escapeHtml(x)}</span>`).join("");
}

function buildPrintableHandoutHtml({
  saint,
  heroImage,
  feastPairs,
  categories,
  tags,
  url,
}) {
  const titleStage = stageLabel(saint);
  const titleName = cleanName(saint?.name);
  const title =
    [titleStage, titleName].filter(Boolean).join(" ").trim() || "Dulia";

  const country = String(saint?.country || saint?.countryLower || "").trim();

  const born = formatDateDMYYY(saint?.bornText);
  const died = formatDateDMYYY(saint?.diedText);
  const beatified = formatDateDMYYY(saint?.beatifiedText);
  const canonized = formatDateDMYYY(saint?.canonizedText);

  const feasts =
    feastPairs?.length === 1
      ? formatFeastDayMonth(feastPairs[0])
      : feastPairs?.length > 1
        ? feastPairs.map(formatFeastDayMonth).join(", ")
        : "";

  // Keep it one-page: truncate longer sections but still show “more online”
  const bio = trimText(saint?.bio, 900);

  const quotesAll = Array.isArray(saint?.quotes)
    ? saint.quotes.filter(Boolean)
    : [];
  const quotes = quotesAll.slice(0, 2).map(normalizeOuterDoubleQuotes);

  const prayersAll = Array.isArray(saint?.prayers)
    ? saint.prayers.filter(Boolean)
    : [];
  const prayer = prayersAll.length ? trimText(prayersAll[0], 500) : "";

  const patronage = Array.isArray(saint?.patronage)
    ? saint.patronage.filter(Boolean)
    : [];

  const hasMore =
    (saint?.bio && String(saint.bio).trim().length > bio.length) ||
    quotesAll.length > quotes.length ||
    prayersAll.length > (prayer ? 1 : 0);

  const safeUrl = escapeHtml(url);

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    /* Print setup */
    @page { margin: 12mm; }
    html, body { height: 100%; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
      color: #111;
      margin: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .wrap { padding: 14px; }
    .top {
      display: flex;
      gap: 14px;
      align-items: flex-start;
    }
    .hero {
      width: 42%;
      border: 1px solid #e5e7eb;
      border-radius: 14px;
      overflow: hidden;
      background: #fff;
      flex: 0 0 auto;
    }
    .hero img {
      width: 100%;
      height: 240px;
      object-fit: cover;
      display: block;
    }
    .hero .imgnote {
      padding: 8px 10px;
      font-size: 11px;
      color: #6b7280;
      border-top: 1px solid #e5e7eb;
      word-break: break-word;
    }

    .meta { flex: 1 1 auto; min-width: 0; }
    .stage { font-size: 14px; font-weight: 700; color: #111827; }
    .name  { font-size: 26px; font-weight: 800; line-height: 1.15; margin-top: 2px; }
    .sub   { font-size: 12px; color: #4b5563; margin-top: 6px; }
    .chips { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 6px; }
    .chip {
      display: inline-flex;
      align-items: center;
      border: 1px solid #e5e7eb;
      border-radius: 999px;
      padding: 2px 8px;
      font-size: 11px;
      color: #111827;
      background: #fff;
      max-width: 100%;
    }

    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 16px;
      margin-top: 14px;
      border: 1px solid #e5e7eb;
      border-radius: 14px;
      padding: 10px 12px;
    }
    .row { display: flex; justify-content: space-between; gap: 10px; }
    .label { font-size: 12px; color: #6b7280; font-weight: 600; }
    .val { font-size: 12px; color: #111827; text-align: right; }

    .section {
      margin-top: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 14px;
      padding: 10px 12px;
    }
    .h {
      font-size: 13px;
      font-weight: 800;
      margin: 0 0 6px 0;
      color: #111827;
    }
    .p {
      font-size: 12px;
      line-height: 1.45;
      color: #111827;
      white-space: pre-wrap;
      margin: 0;
    }
    .quote {
      border-left: 3px solid #e5e7eb;
      padding-left: 10px;
      margin: 8px 0 0 0;
      white-space: pre-wrap;
      font-size: 12px;
      line-height: 1.4;
    }

    .footer {
      margin-top: 10px;
      font-size: 11px;
      color: #6b7280;
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: baseline;
      word-break: break-word;
    }
    .footer a { color: inherit; text-decoration: none; }
    .footer a:hover { text-decoration: underline; }

    /* Avoid page breaks inside blocks */
    .hero, .grid, .section { break-inside: avoid; page-break-inside: avoid; }

    /* Hide anything interactive (none here, but safe) */
    @media print {
      a[href]:after { content: ""; }
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="top">
      <div class="hero">
        ${heroImage
          ? `<img src="${escapeHtml(heroImage)}" alt="${escapeHtml(title)}" />`
          : `<div style="height:240px;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:12px;">No image</div>`
        }
        ${heroImage
          ? `<div class="imgnote">Image source: ${escapeHtml(
              getHostname(heroImage)
            )}</div>`
          : ``
        }
      </div>

      <div class="meta">
        ${titleStage ? `<div class="stage">${escapeHtml(titleStage)}</div>` : ``}
        <div class="name">${escapeHtml(
          titleName || saint?.name || "Dulia"
        )}</div>
        ${country ? `<div class="sub">${escapeHtml(country)}</div>` : ``}

        <div class="chips">
          ${renderChips(categories)}
          ${renderChips((tags || []).slice(0, 10))}
        </div>

        <div class="grid">
          ${born
            ? `<div class="row"><div class="label">Born</div><div class="val">${escapeHtml(
                born
              )}</div></div>`
            : ``
          }
          ${died
            ? `<div class="row"><div class="label">Died</div><div class="val">${escapeHtml(
                died
              )}</div></div>`
            : ``
          }
          ${beatified
            ? `<div class="row"><div class="label">Beatified</div><div class="val">${escapeHtml(
                beatified
              )}</div></div>`
            : ``
          }
          ${canonized
            ? `<div class="row"><div class="label">Canonized</div><div class="val">${escapeHtml(
                canonized
              )}</div></div>`
            : ``
          }
          ${feasts
            ? `<div class="row"><div class="label">${
                feastPairs?.length > 1 ? "Feasts" : "Feast"
              }</div><div class="val">${escapeHtml(feasts)}</div></div>`
            : ``
          }
          ${patronage.length
            ? `<div class="row" style="grid-column:1 / -1;">
                <div class="label">Patronage</div>
                <div class="val" style="text-align:right;">${escapeHtml(
                  patronage.slice(0, 6).join(", ")
                )}${patronage.length > 6 ? "…" : ""}</div>
              </div>`
            : ``
          }
        </div>
      </div>
    </div>

    ${bio
      ? `<div class="section">
            <div class="h">Bio</div>
            <p class="p">${escapeHtml(bio)}</p>
          </div>`
      : ``
    }

    ${quotes.length
      ? `<div class="section">
            <div class="h">Quotes</div>
            ${quotes
              .map(
                (q) =>
                  `<div class="quote">“${escapeHtml(
                    String(q).replace(/^"+|"+$/g, "")
                  )}”</div>`
              )
              .join("")}
          </div>`
      : ``
    }

    ${prayer
      ? `<div class="section">
            <div class="h">Prayer</div>
            <p class="p">${escapeHtml(prayer)}</p>
          </div>`
      : ``
    }

    <div class="footer">
      <div>
        ${hasMore ? "More content online:" : "Profile:"}
        <a href="${safeUrl}">${safeUrl}</a>
      </div>
      <div>Handout • Dulia</div>
    </div>
  </div>

  <script>
    (async function () {
      const imgs = Array.from(document.images || []);
      await Promise.all(imgs.map(img => new Promise(res => {
        if (img.complete) return res();
        img.onload = img.onerror = () => res();
      })));
      window.focus();
      window.print();
      window.onafterprint = () => window.close();
    })();
  </script>
</body>
</html>`;
}

/** ---- PDF helpers (jsPDF) ---- */
function makePublicDuliaUrl(href) {
  try {
    const u = new URL(String(href));
    u.protocol = "https:";
    u.hostname = "dulia.us";
    u.port = "";
    return u.toString();
  } catch {
    return String(href || "");
  }
}

function toDuliaDisplayUrl(href) {
  try {
    const u = new URL(makePublicDuliaUrl(href));
    return `${u.hostname}${u.pathname}`;
  } catch {
    return "dulia.us";
  }
}

// jsPDF default fonts are WinAnsi-only. Strip emoji/surrogates and normalize quotes/dashes.
function sanitizePdfText(s) {
  return String(s ?? "")
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "") // surrogate pairs (emoji, flags)
    .replace(/\uFE0F/g, "") // variation selector
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...");
}

async function fetchImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!/^image\/(png|jpe?g)$/i.test(blob.type)) return null;

    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function dataUrlToJsPdfFormat(dataUrl) {
  if (!dataUrl) return null;
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (
    dataUrl.startsWith("data:image/jpeg") ||
    dataUrl.startsWith("data:image/jpg")
  )
    return "JPEG";
  return null;
}

function canvasRoundedRectPath(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

async function makeSquareCoverDataUrl(imageUrl, canvasSize = 900) {
  const src = await fetchImageAsDataUrl(imageUrl);
  if (!src) return null;

  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) return resolve(null);

        const canvas = document.createElement("canvas");
        canvas.width = canvasSize;
        canvas.height = canvasSize;

        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);

        // Rounded corners baked into the image (matches profile-like look)
        const r = Math.round(canvasSize * 0.066);

        ctx.clearRect(0, 0, canvasSize, canvasSize);
        ctx.save();
        canvasRoundedRectPath(ctx, 0, 0, canvasSize, canvasSize, r);
        ctx.clip();

        // object-cover with object-position: top
        const scale = Math.max(canvasSize / w, canvasSize / h);
        const drawW = w * scale;
        const drawH = h * scale;
        const dx = (canvasSize - drawW) / 2;
        const dy = 0; // top-aligned

        ctx.drawImage(img, dx, dy, drawW, drawH);
        ctx.restore();

        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function fitTextToLines(doc, text, width, maxLines) {
  const lines = doc.splitTextToSize(String(text || ""), width) || [];
  if (lines.length <= maxLines) return lines;
  const trimmed = lines.slice(0, maxLines);
  trimmed[trimmed.length - 1] =
    String(trimmed[trimmed.length - 1]).replace(/\s+$/, "") + "…";
  return trimmed;
}

function drawChips(doc, { x, y, maxW, items, fontSize = 9.5 }) {
  const arr = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!arr.length) return y;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);

  const padX = 8;
  const padY = 4;
  const gapX = 6;
  const gapY = 6;
  const r = 10;

  let cx = x;
  let cy = y;

  for (const t of arr) {
    const text = sanitizePdfText(String(t));
    if (!text) continue;

    const tw = doc.getTextWidth(text);
    const w = tw + padX * 2;
    const h = fontSize + padY * 2;

    if (cx + w > x + maxW) {
      cx = x;
      cy += h + gapY;
    }

    doc.setDrawColor(229, 231, 235);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cx, cy, w, h, r, r, "FD");

    doc.setTextColor(0, 0, 0);
    doc.text(text, cx + padX, cy + h - padY - 1);

    cx += w + gapX;
  }

  return cy + (fontSize + padY * 2);
}

function drawCard(doc, { x, y, w, h, radius = 14 }) {
  doc.setDrawColor(229, 231, 235);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, radius, radius, "FD");
}

function formatValueOrDash(value) {
  const v = String(value ?? "").trim();
  return v ? sanitizePdfText(v) : "-";
}

async function generateHandoutPdfBlob({
  saint,
  heroImage,
  feastPairs,
  categories,
  tags,
  url,
}) {
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const margin = 40;
  const gap = 18;

  // Footer sizing (reserve space so content never overlaps footer)
  const footerH = 64;
  const contentBottomY = pageH - margin - footerH;

  // Build public link (always dulia.us)
  const publicUrl = makePublicDuliaUrl(url);
  const displayUrl = toDuliaDisplayUrl(url);

  // ---- Prepare hero image square (object-cover/top) with rounded corners baked in ----
  let heroSquare = null;
  let heroFmt = null;
  if (heroImage) {
    heroSquare = await makeSquareCoverDataUrl(heroImage, 900);
    heroFmt = dataUrlToJsPdfFormat(heroSquare);
  }

  // ---- Common strings ----
  const stage = sanitizePdfText(stageLabel(saint));
  const clean = sanitizePdfText(cleanName(saint?.name) || saint?.name || "Dulia");
  const fullName = sanitizePdfText(saint?.name || clean);

  const country = sanitizePdfText(
    String(saint?.country || saint?.countryLower || "").trim()
  );

  function normalizePdfWhitespace(s) {
    return sanitizePdfText(String(s || ""))
      // convert NBSP + other unicode spaces to normal spaces
      .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, " ")
      // remove zero-width chars
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      // collapse runs of spaces/tabs
      .replace(/[ \t]+/g, " ")
      // kill leading indentation that only matters in HTML
      .replace(/^\s+/gm, "");
  }

  function wrapPreserveNewlines(doc, text, width) {
    const t = normalizePdfWhitespace(text).replace(/\r\n/g, "\n");
    if (!t.trim()) return [];

    const parts = t.split("\n");
    const out = [];

    for (const raw of parts) {
      // keep blank lines as blank lines
      if (!raw.trim()) {
        out.push("");
        continue;
      }

      // wrap this paragraph-line
      const wrapped = doc.splitTextToSize(raw, width) || [];
      out.push(...wrapped);
    }

    return out;
  }

  // ---- Page header for continuation pages (small, clean) ----
  function drawMiniHeader() {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Dulia: ${fullName}`, margin, margin - 12, {
      maxWidth: pageW - margin * 2,
    });

    pdf.setDrawColor(229, 231, 235);
    pdf.line(margin, margin - 4, pageW - margin, margin - 4);
  }

  // ---- Footer (applied to every page at the end) ----
  function drawFooterOnPage(pageIndex, pageCount, logoData, logoFmt) {
    const footerTop = pageH - margin - footerH + 8;

    // Divider line
    pdf.setDrawColor(229, 231, 235);
    pdf.line(margin, footerTop - 10, pageW - margin, footerTop - 10);

    // Learn more text
    const learnText = `Learn more about ${fullName} by reading their full profile at ${displayUrl}.`;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.8);
    pdf.setTextColor(0, 0, 0);

    const learnMaxW = pageW - margin * 2 - 170;
    const learnLines = fitTextToLines(
      pdf,
      sanitizePdfText(learnText),
      learnMaxW,
      2
    );
    pdf.text(learnLines, margin, footerTop + 10);

    // Clickable link annotation for the learn-more area
    try {
      pdf.link(margin, footerTop - 2, learnMaxW, 30, { url: publicUrl });
    } catch {
      // ignore
    }

    // Logo + label
    const logoW = 44;
    const logoH = 44;
    const logoX = pageW - margin - logoW;
    const logoY = pageH - margin - logoH - 14;

    if (logoData && logoFmt) {
      try {
        pdf.addImage(logoData, logoFmt, logoX, logoY, logoW, logoH);
      } catch {
        // ignore
      }
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    const createdText = "PDF created by Dulia";
    const createdW = pdf.getTextWidth(createdText);
    pdf.text(createdText, logoX + (logoW - createdW) / 2, logoY + logoH + 12);

    // Page number (bottom right-ish but not under logo)
    const pageText = `Page ${pageIndex} of ${pageCount}`;
    const pageTextW = pdf.getTextWidth(pageText);
    pdf.text(
      pageText,
      pageW - margin - logoW - 14 - pageTextW,
      pageH - margin - 6
    );
  }

  // ---- Layout: Page 1 Top section (same as your current design) ----
  const topY = margin;
  const imgSize = 270;
  const imgX = margin;
  const imgY = topY;

  const rightX = imgX + imgSize + gap;
  const rightW = pageW - margin - rightX;

  // Image card
  drawCard(pdf, { x: imgX, y: imgY, w: imgSize, h: imgSize, radius: 18 });
  if (heroSquare && heroFmt) {
    pdf.addImage(heroSquare, heroFmt, imgX, imgY, imgSize, imgSize);
  } else {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text("No hero image", imgX + 14, imgY + 28);
  }

  // Right header block: stage + big name + country
  let ry = imgY + 10;

  if (stage) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text(stage, rightX, ry + 14, { maxWidth: rightW });
    ry += 18;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(26);
  pdf.setTextColor(0, 0, 0);
  const nameLines = fitTextToLines(pdf, clean, rightW, 2).map(sanitizePdfText);
  pdf.text(nameLines, rightX, ry + 26);
  ry += nameLines.length * 28;

  if (country) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text(country, rightX, ry + 6, { maxWidth: rightW });
    ry += 18;
  } else {
    ry += 8;
  }

  // Chips
  const chipItems = [
    ...(Array.isArray(categories) ? categories : []),
    ...((Array.isArray(tags) ? tags : []).slice(0, 10)),
  ].filter(Boolean);

  if (chipItems.length) {
    ry = drawChips(pdf, { x: rightX, y: ry, maxW: rightW, items: chipItems });
    ry += 10;
  } else {
    ry += 10;
  }

  // Details card
  const detailsTitle = "Details";
  const detailsY = Math.min(imgY + imgSize - 168, Math.max(ry, imgY + 160));
  const detailsH = imgY + imgSize - detailsY;

  drawCard(pdf, { x: rightX, y: detailsY, w: rightW, h: detailsH, radius: 18 });

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12.5);
  pdf.setTextColor(0, 0, 0);
  pdf.text(detailsTitle, rightX + 14, detailsY + 22);

  const born = formatValueOrDash(formatDateDMYYY(saint?.bornText));
  const died = formatValueOrDash(formatDateDMYYY(saint?.diedText));
  const beatified = formatValueOrDash(formatDateDMYYY(saint?.beatifiedText));
  const canonized = formatValueOrDash(formatDateDMYYY(saint?.canonizedText));

  const feasts =
    feastPairs?.length === 1
      ? formatFeastDayMonth(feastPairs[0])
      : feastPairs?.length > 1
        ? feastPairs.map(formatFeastDayMonth).join(", ")
        : "";
  const feastValue = formatValueOrDash(feasts);

  const patronage = Array.isArray(saint?.patronage)
    ? saint.patronage.filter(Boolean)
    : [];
  const patronageValue = patronage.length
    ? sanitizePdfText(patronage.join(", "))
    : "-";

  const colW = (rightW - 28) / 2;
  const leftX = rightX + 14;
  const rightColX = rightX + 14 + colW + 14;

  const rowY0 = detailsY + 44;
  let dy = rowY0;

  function kv(x, y, label, value, maxW) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(label, x, y);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    pdf.setTextColor(0, 0, 0);
    const lines = fitTextToLines(pdf, sanitizePdfText(value), maxW, 2);
    pdf.text(lines, x, y + 14);
    return y + 14 + lines.length * 12 + 6;
  }

  const blockStart = dy;
  let leftY = blockStart;
  let rightY = blockStart;

  leftY = kv(leftX, leftY, "Born", born, colW);
  leftY = kv(leftX, leftY, "Beatified", beatified, colW);
  leftY = kv(
    leftX,
    leftY,
    feastPairs?.length > 1 ? "Feasts" : "Feast",
    feastValue,
    colW
  );

  rightY = kv(rightColX, rightY, "Died", died, colW);
  rightY = kv(rightColX, rightY, "Canonized", canonized, colW);

  const patronageY = Math.max(leftY, rightY);
  const patronageMaxW = rightW - 28;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text("Patronage", leftX, patronageY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10.5);
  pdf.setTextColor(0, 0, 0);
  const patronageLines = fitTextToLines(pdf, patronageValue, patronageMaxW, 2);
  pdf.text(patronageLines, leftX, patronageY + 14);

  // ---- Content flow system (multi-page) ----
  let y = imgY + imgSize + 22;

  pdf.setDrawColor(229, 231, 235);
  pdf.line(margin, y, pageW - margin, y);
  y += 16;

  const fullW = pageW - margin * 2;

  function ensureSpace(minNeeded) {
    if (y + minNeeded <= contentBottomY) return;
    pdf.addPage();
    y = margin;
    drawMiniHeader();
    y += 18; // give header breathing room
  }

  function addCardPaged({ title, body, titleCont }) {
    const pad = 14;
    const titleH = 18;
    const lineH = 13;

    const wrapW = fullW - pad * 2 - 8;

    const allLines = wrapPreserveNewlines(pdf, body, wrapW);
    if (!allLines.length) return;

    let first = true;
    let lines = allLines.slice();

    while (lines.length) {
      // Start a new page if we can't fit even a minimal card
      ensureSpace(90);

      const availH = contentBottomY - y;
      const maxBodyLines = Math.max(
        1,
        Math.floor((availH - pad * 2 - titleH) / lineH)
      );

      if (maxBodyLines <= 0) {
        pdf.addPage();
        y = margin;
        drawMiniHeader();
        y += 18;
        continue;
      }

      const chunk = lines.splice(0, maxBodyLines);

      const bodyH = chunk.length * lineH;
      const h = pad * 2 + titleH + bodyH;

      drawCard(pdf, { x: margin, y, w: fullW, h, radius: 18 });

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12.5);
      pdf.setTextColor(0, 0, 0);

      const t = first ? title : (titleCont || `${title} (cont.)`);
      pdf.text(t, margin + pad, y + pad + 12);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.8);
      pdf.setTextColor(0, 0, 0);
      pdf.text(chunk, margin + pad, y + pad + 12 + titleH);

      y += h + 12;
      first = false;
    }
  }

  // ---- Add FULL sections (no trimming / no slicing) ----
  if (saint?.bio) {
    addCardPaged({ title: "Bio", body: saint.bio });
  }

  const quotesAll = Array.isArray(saint?.quotes) ? saint.quotes.filter(Boolean) : [];
  if (quotesAll.length) {
    quotesAll.forEach((q, idx) => {
      const qq = normalizeOuterDoubleQuotes(q);
      addCardPaged({
        title: `Quote ${idx + 1}`,
        body: qq,
        titleCont: `Quote ${idx + 1} (cont.)`,
      });
    });
  }

  const prayersAll = Array.isArray(saint?.prayers) ? saint.prayers.filter(Boolean) : [];
  if (prayersAll.length) {
    prayersAll.forEach((p, idx) => {
      addCardPaged({
        title: `Prayer ${idx + 1}`,
        body: p,
        titleCont: `Prayer ${idx + 1} (cont.)`,
      });
    });
  }

  const contributors = Array.isArray(saint?.contributors)
    ? saint.contributors.filter(Boolean)
    : [];
  if (contributors.length) {
    addCardPaged({
      title: "Contributors",
      body: `Profile created with contributions by ${formatContributorList(contributors)}.`,
    });
  }

  // ---- Fetch logo once, then apply footers to every page ----
  let logoData =
    (await fetchImageAsDataUrl("/logo192.png")) ||
    (await fetchImageAsDataUrl("/logo512.png"));
  const logoFmt = dataUrlToJsPdfFormat(logoData);

  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    drawFooterOnPage(i, pageCount, logoData, logoFmt);
  }

  // PDF metadata title
  try {
    pdf.setProperties({
      title: `Dulia: ${saint?.name ? String(saint.name) : fullName}`,
      subject: "Dulia profile summary",
    });
  } catch {
    // ignore
  }

  return pdf.output("blob");
}

function FieldRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex min-w-0 items-start justify-between gap-3 py-2.5">
      <div className="shrink-0 text-sm font-medium text-dulia-navy/75 dark:text-dulia-parchment/70">
        {label}
      </div>
      <div className="min-w-0 break-words text-right text-sm text-dulia-ink dark:text-dulia-parchment">
        {value}
      </div>
    </div>
  );
}

function stageLabel(saint) {
  if (!saint) return "";
  const stage = String(saint.stage || "").trim();
  if (!stage) return "";
  if (saint.name.includes("Martyrs")) return "";
  if (saint.isPope) return `Pope ${stage}`.trim();

  const pluralStage = stage.endsWith("s") ? stage : `${stage}s`;
  return String(saint.name || "").includes(pluralStage) ? pluralStage : stage;
}

function cleanName(name) {
  return String(name || "")
    .replace("Saints", "")
    .replace("Saint", "")
    .replace("Blessed", "")
    .replace("Venerable", "")
    .replace("Servant of God", "")
    .replace("Pope", "")
    .trim();
}

function normalizeOuterDoubleQuotes(s) {
  if (s === null || s === undefined) return "";
  let t = String(s).trim();
  if (!t) return t;

  t = t.replace(/^[\u0022\u201C\u201D]+/, "");
  t = t.replace(/[\u0022\u201C\u201D]+$/, "");

  return `"${t}"`;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    const ok = await copyText(text);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      className="rounded-xl border border-dulia-line bg-white/85 px-2.5 py-1 text-xs font-medium text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
      title={label}
    >
      {copied ? "Copied" : label}
    </button>
  );
}

function getHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "source";
  }
}

function SourceLink({ url }) {
  if (!url) return null;
  const host = getHostname(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-[11px] text-dulia-navy/70 underline decoration-dulia-lineStrong/50 underline-offset-4 hover:text-dulia-navy dark:text-dulia-parchment/70 dark:decoration-dulia-gold/40 dark:hover:text-dulia-goldSoft"
      title={url}
    >
      Source: {host}
    </a>
  );
}

/** ---- Modal carousel ---- */
function ImageCarouselModal({
  open,
  images,
  index,
  onClose,
  onPrev,
  onNext,
  title,
}) {
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    if (!open) return;
    setImgOk(true);
  }, [open, index]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };

    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;

  const url = images?.[index] || "";

  const caption = (() => {
    const caps = Array.isArray(images?.captions) ? images.captions : null;
    return null;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-dulia-night/80 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close image viewer"
      />

      {/* Panel */}
      <div className="relative w-[calc(100%-1rem)] max-w-5xl overflow-hidden rounded-[24px] border border-dulia-line bg-dulia-panel shadow-card dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft sm:w-[calc(100%-2rem)] sm:rounded-[28px]">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dulia-line bg-dulia-parchment/55 px-3 py-3 dark:border-dulia-lineStrong/30 dark:bg-dulia-night sm:px-4">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              {title || "Images"}
            </div>
            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/70">
              {images?.length ? `${index + 1} / ${images.length}` : ""}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {url ? (
              <button
                type="button"
                className="rounded-xl border border-dulia-line bg-white/85 px-3 py-1.5 text-xs font-medium text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
                onClick={() => copyText(url)}
                title="Copy image URL"
              >
                Copy URL
              </button>
            ) : null}

            <button
              type="button"
              className="rounded-xl border border-dulia-line bg-white/85 px-3 py-1.5 text-xs font-medium text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="relative bg-black">
          {images?.length > 1 && (
            <>
              <button
                type="button"
                onClick={onPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-dulia-line bg-white/90 px-3 py-2 text-sm font-medium text-dulia-navy shadow-soft transition hover:bg-dulia-parchment"
                aria-label="Previous image"
              >
                ←
              </button>
              <button
                type="button"
                onClick={onNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-dulia-line bg-white/90 px-3 py-2 text-sm font-medium text-dulia-navy shadow-soft transition hover:bg-dulia-parchment"
                aria-label="Next image"
              >
                →
              </button>
            </>
          )}

          <div className="flex items-center justify-center p-3 sm:p-4">
            {url && imgOk ? (
              <img
                src={url}
                alt={title || "Image"}
                className="max-h-[75vh] w-auto max-w-full object-contain"
                onError={() => setImgOk(false)}
              />
            ) : (
              <div className="flex h-[55vh] w-full items-center justify-center text-sm text-white/80">
                Image failed to load.
              </div>
            )}
          </div>
        </div>

        {url ? (
          <div className="flex flex-col gap-2 border-t border-dulia-line bg-dulia-parchment/40 px-3 py-3 dark:border-dulia-lineStrong/30 dark:bg-dulia-night sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <SourceLink url={url} />
            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/70 sm:text-right">
              Use keyboard arrows or click buttons to navigate, Esc to close
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** ---- OpenGraph helpers (runtime meta tags) ---- */
function toAbsoluteUrl(url) {
  if (!url) return "";
  try {
    return new URL(String(url), window.location.origin).toString();
  } catch {
    return String(url);
  }
}

function upsertMeta({ property, name, content }) {
  if (!content) return;

  const selector = property
    ? `meta[property="${property}"]`
    : `meta[name="${name}"]`;

  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (property) el.setAttribute("property", property);
    if (name) el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** ---- Date formatting (d MMMM YYYY) ---- */
const MONTHS = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

function parseDateTextToUTCDate(value) {
  if (!value) return null;
  const s = String(value).trim();

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (
      dt.getUTCFullYear() === y &&
      dt.getUTCMonth() === m - 1 &&
      dt.getUTCDate() === d
    ) {
      return dt;
    }
    return null;
  }

  const text = s
    .replace(/,/g, "")
    .match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (text) {
    const d = Number(text[1]);
    const monthName = text[2].toLowerCase();
    const y = Number(text[3]);
    const m = MONTHS[monthName];
    if (!m) return null;
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (
      dt.getUTCFullYear() === y &&
      dt.getUTCMonth() === m - 1 &&
      dt.getUTCDate() === d
    ) {
      return dt;
    }
    return null;
  }

  return null;
}

function formatDateDMYYY(value) {
  const dt = parseDateTextToUTCDate(value);
  if (!dt) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(dt);
}

/** ---- Feast formatting (multi) ---- */
function parseFeastKey(key) {
  if (!key) return null;
  const s = String(key).trim();
  if (!/^\d{2}-\d{2}$/.test(s)) return null;
  const m = Number(s.slice(0, 2));
  const d = Number(s.slice(3, 5));
  if (!m || !d) return null;
  return { month: m, day: d, key: s };
}

function getFeastPairs(saint) {
  if (Array.isArray(saint.feastKeys) && saint.feastKeys.length) {
    return saint.feastKeys.map(parseFeastKey).filter(Boolean);
  }
  if (Array.isArray(saint.feasts) && saint.feasts.length) {
    return saint.feasts.map(parseFeastKey).filter(Boolean);
  }
  if (saint.feastKey) {
    const p = parseFeastKey(saint.feastKey);
    return p ? [p] : [];
  }
  if (saint.feastMonth && saint.feastDay) {
    const mm = String(Number(saint.feastMonth)).padStart(2, "0");
    const dd = String(Number(saint.feastDay)).padStart(2, "0");
    return [
      {
        month: Number(saint.feastMonth),
        day: Number(saint.feastDay),
        key: `${mm}-${dd}`,
      },
    ];
  }
  return [];
}

function formatFeastDayMonth(pair) {
  const dt = new Date(Date.UTC(2000, pair.month - 1, pair.day));
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(dt);
}

export default function ProfilePage() {
  const { id } = useParams();
  const [saint, setSaint] = useState(undefined); // undefined = loading
  const [heroOk, setHeroOk] = useState(true);
  const [shareStatus, setShareStatus] = useState("");
  const { user, admin, adminLoading } = useAuth();
  const isAdmin = admin;

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // NEW: favorite toggle state
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState("bio");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (adminLoading) return;
      const ref = doc(db, "saints", id);

      const snap = await getDoc(ref);
      if (cancelled) return;

      if (snap.exists()) {
        setSaint({ id: snap.id, ...snap.data() });
        const data = snap.data();

        // ✅ Block pending profiles for non-admins
        if (data?.approved === false && !isAdmin) {
          setSaint(null);
          return;
        }

        setSaint({ id: snap.id, ...data });

        // increment views (creates field if missing)
        updateDoc(ref, { views: increment(1) }).catch(() => {
          // ignore (e.g., permission rules)
        });
      } else {
        setSaint(null);
      }

      setHeroOk(true);
      setShareStatus("");
      setViewerOpen(false);
      setViewerIndex(0);
      setSavingFavorite(false);
      setActiveTab("bio");
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isAdmin, adminLoading]);

  const galleryImages = useMemo(() => {
    const g = Array.isArray(saint?.gallery) ? saint.gallery.filter(Boolean) : [];
    return g;
  }, [saint]);

  const galleryCaptions = useMemo(() => {
    const caps = Array.isArray(saint?.galleryCaptions)
      ? saint.galleryCaptions
      : [];
    return caps;
  }, [saint]);

  const heroImage = useMemo(
    () => (galleryImages.length ? galleryImages[0] : null),
    [galleryImages]
  );

  const heroCaption = useMemo(() => {
    const c = galleryCaptions?.[0];
    const t = String(c ?? "").trim();
    return t || "";
  }, [galleryCaptions]);

  const feastPairs = useMemo(() => (saint ? getFeastPairs(saint) : []), [saint]);

  const categories = useMemo(() => {
    const cats = Array.isArray(saint?.categories) ? saint.categories : [];
    return cats;
  }, [saint]);

  const tags = useMemo(() => {
    const t = saint?.tags;
    return Array.isArray(t) ? t : [];
  }, [saint]);

  const contributors = useMemo(() => {
    const arr = Array.isArray(saint?.contributors)
      ? saint.contributors.filter(Boolean)
      : [];
    return arr;
  }, [saint]);

  const patronage = useMemo(() => {
    const arr = Array.isArray(saint?.patronage)
      ? saint.patronage.filter(Boolean)
      : [];
    return arr;
  }, [saint]);

  // Favorites + views
  const favoritesArr = useMemo(
    () => (Array.isArray(saint?.favorites) ? saint.favorites.filter(Boolean) : []),
    [saint]
  );
  const viewsCount = Number(saint?.views || 0) + 1; // + 1 for current viewer
  const favoritesCount = favoritesArr.length;

  const userEmail = String(user?.email || "").trim();
  const isSignedIn = !!user && !!userEmail;
  const isFavorited = isSignedIn && favoritesArr.includes(userEmail);

  async function onToggleFavorite() {
    // ---- quick sanity logs ----
    console.log("[fav] toggle clicked", {
      saintLoaded: !!saint,
      saintId: id,
      isSignedIn,
      userEmail,
      isFavorited,
      savingFavorite,
    });
  
    if (!saint) return;
  
    if (!isSignedIn) {
      console.log("[fav] blocked: not signed in");
      setShareStatus("Sign in to save favorites");
      window.setTimeout(() => setShareStatus(""), 1500);
      return;
    }
  
    if (savingFavorite) {
      console.log("[fav] blocked: already saving");
      return;
    }
  
    const email = String(userEmail || "").trim();
    if (!email) {
      console.warn("[fav] blocked: empty email");
      return;
    }
  
    setSavingFavorite(true);
  
    const wasFav = isFavorited;
  
    const saintRef = doc(db, "saints", id);
    const userRef = doc(db, "users", email);
  
    console.log("[fav] refs", {
      saintPath: saintRef.path,
      userPath: userRef.path,
      action: wasFav ? "UNFAVORITE" : "FAVORITE",
    });
  
    // ---- optimistic UI update (saint favorites count) ----
    setSaint((prev) => {
      if (!prev) return prev;
      const prevFavs = Array.isArray(prev.favorites) ? prev.favorites : [];
      const nextFavs = wasFav
        ? prevFavs.filter((x) => x !== email)
        : prevFavs.includes(email)
          ? prevFavs
          : [...prevFavs, email];
  
      console.log("[fav] optimistic update", {
        prevLen: prevFavs.length,
        nextLen: nextFavs.length,
      });
  
      return { ...prev, favorites: nextFavs };
    });
  
    try {
      // Helpful: confirm the user doc exists + print its current favorites (if allowed)
      try {
        const userSnap = await getDoc(userRef);
        console.log("[fav] user doc pre-read", {
          exists: userSnap.exists(),
          id: userSnap.id,
          favorites: userSnap.exists() ? userSnap.data()?.favorites : undefined,
        });
      } catch (e) {
        console.warn("[fav] user doc pre-read failed", e);
      }
  
      if (wasFav) {
        console.log("[fav] writing arrayRemove to both docs");
        const results = await Promise.allSettled([
          updateDoc(saintRef, { favorites: arrayRemove(email) }),
          updateDoc(userRef, { favorites: arrayRemove(id) }),
        ]);
  
        console.log("[fav] write results (unfavorite)", results);
  
        results.forEach((r, idx) => {
          if (r.status === "rejected") {
            console.error(
              `[fav] write failed: ${idx === 0 ? "saintRef" : "userRef"}`,
              r.reason
            );
          }
        });
      } else {
        console.log("[fav] writing arrayUnion to both docs");
        const results = await Promise.allSettled([
          updateDoc(saintRef, { favorites: arrayUnion(email) }),
          updateDoc(userRef, { favorites: arrayUnion(id) }),
        ]);
  
        console.log("[fav] write results (favorite)", results);
  
        results.forEach((r, idx) => {
          if (r.status === "rejected") {
            console.error(
              `[fav] write failed: ${idx === 0 ? "saintRef" : "userRef"}`,
              r.reason
            );
          }
        });
      }
  
      // Helpful: confirm post-write state (if allowed)
      try {
        const userSnap2 = await getDoc(userRef);
        console.log("[fav] user doc post-read", {
          exists: userSnap2.exists(),
          id: userSnap2.id,
          favorites: userSnap2.exists() ? userSnap2.data()?.favorites : undefined,
        });
      } catch (e) {
        console.warn("[fav] user doc post-read failed", e);
      }
    } catch (err) {
      console.error("[fav] unexpected error", err);
  
      // revert optimistic UI update
      setSaint((prev) => {
        if (!prev) return prev;
        const prevFavs = Array.isArray(prev.favorites) ? prev.favorites : [];
        const nextFavs = wasFav
          ? prevFavs.includes(email)
            ? prevFavs
            : [...prevFavs, email]
          : prevFavs.filter((x) => x !== email);
  
        console.log("[fav] revert optimistic update", {
          prevLen: prevFavs.length,
          nextLen: nextFavs.length,
        });
  
        return { ...prev, favorites: nextFavs };
      });
  
      setShareStatus("Failed to update favorites");
      window.setTimeout(() => setShareStatus(""), 1800);
    } finally {
      setSavingFavorite(false);
      console.log("[fav] done");
    }
  }
  

  function openViewerAt(i) {
    if (!galleryImages.length) return;
    const idx = Math.max(0, Math.min(i, galleryImages.length - 1));
    setViewerIndex(idx);
    setViewerOpen(true);
  }

  function closeViewer() {
    setViewerOpen(false);
  }

  function prevImage() {
    if (!galleryImages.length) return;
    setViewerIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  }

  function nextImage() {
    if (!galleryImages.length) return;
    setViewerIndex((i) => (i + 1) % galleryImages.length);
  }

  useEffect(() => {
    const baseTitle = saint?.name ? saint.name : "Dulia";
    const url = toAbsoluteUrl(window.location.href);

    document.title = saint?.name ? `Dulia: ${saint.name}` : "Dulia";

    upsertMeta({ property: "og:title", content: baseTitle });
    upsertMeta({ property: "og:type", content: "website" });
    upsertMeta({ property: "og:url", content: url });

    if (heroImage) {
      const img = toAbsoluteUrl(heroImage);
      upsertMeta({ property: "og:image", content: img });
      upsertMeta({ name: "twitter:image", content: img });
      upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    } else {
      upsertMeta({ name: "twitter:card", content: "summary" });
    }
  }, [saint, heroImage]);

  async function onGeneratePdf() {
    if (!saint) return;

    // Keep legacy helper referenced (so it won't be flagged unused in strict lint configs)
    void buildPrintableHandoutHtml;

    const tab = window.open("", "_blank");
    if (!tab) {
      setShareStatus("Pop-up blocked (allow pop-ups to generate PDF)");
      window.setTimeout(() => setShareStatus(""), 2200);
      return;
    }

    try {
      tab.opener = null;
    } catch {
      // ignore
    }

    tab.document.open();
    tab.document.write(`<!doctype html>
<html><head><meta charset="utf-8" /><title>Generating PDF…</title></head>
<body style="font-family:system-ui;margin:0;padding:16px;background:#fff;color:#000;">
  <div>Generating PDF…</div>
</body></html>`);
    tab.document.close();

    try {
      const pdfBlob = await generateHandoutPdfBlob({
        saint,
        heroImage: heroOk ? heroImage : null,
        feastPairs,
        categories,
        tags,
        url: window.location.href,
      });

      const blobUrl = URL.createObjectURL(pdfBlob);
      const title = saint?.name ? `Dulia: ${saint.name}` : "Dulia";

      tab.document.open();
      tab.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    html, body { height: 100%; margin: 0; background: #fff; }
    embed { width: 100%; height: 100%; border: 0; }
  </style>
</head>
<body>
  <embed src="${blobUrl}" type="application/pdf" />
  <script>
    window.addEventListener("beforeunload", function () {
      try { URL.revokeObjectURL(${JSON.stringify(blobUrl)}); } catch {}
    });
  </script>
</body>
</html>`);
      tab.document.close();
    } catch (err) {
      tab.document.body.innerHTML = `<pre style="white-space:pre-wrap;padding:16px;background:#fff;color:#000;">Failed to generate PDF.\n\n${escapeHtml(
        String(err)
      )}</pre>`;
    }
  }

  async function onShare() {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: saint?.name || "Dulia",
          text: saint?.name || "Dulia",
          url,
        });
        return;
      } catch {
        // fall back to copy
      }
    }
    const ok = await copyText(url);
    setShareStatus(ok ? "Link copied" : "Copy failed");
    window.setTimeout(() => setShareStatus(""), 1500);
  }

  if (saint === undefined) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 lg:py-10">
        <div className="rounded-[28px] border border-dulia-line bg-dulia-panel p-6 text-sm text-dulia-navy shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment">
          Loading…
        </div>
      </div>
    );
  }

  if (saint === null) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-4 py-8 md:px-6 lg:py-10">
        <div className="rounded-[28px] border border-dulia-line bg-dulia-panel p-6 shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft">
          <h1 className="text-2xl font-bold text-dulia-ink dark:text-dulia-parchment">
            Not found
          </h1>
          <p className="mt-2 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
            This profile could not be loaded.
          </p>
          <Link
            className="mt-4 inline-flex items-center rounded-xl border border-dulia-line bg-white/85 px-4 py-2 text-sm font-medium text-dulia-navy transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
            to="/search"
          >
            ← Back to search
          </Link>
        </div>
      </div>
    );
  }

  const favoriteChipBase =
    "inline-flex items-center gap-1.5 rounded-full border border-dulia-gold/35 bg-dulia-gold/10 px-3 py-1.5 text-xs font-semibold text-dulia-navy " +
    "dark:border-dulia-gold/30 dark:bg-dulia-gold/15 dark:text-dulia-goldSoft";

  const favoriteChipInteractive = isSignedIn
    ? "cursor-pointer hover:bg-dulia-gold/20 dark:hover:bg-dulia-gold/20"
    : "cursor-help";

  const favoriteChipDisabled = savingFavorite ? "opacity-60" : "";

  const statChip =
    "inline-flex items-center gap-1.5 rounded-full border border-dulia-line bg-white/90 px-3 py-1.5 text-xs font-semibold text-dulia-ink " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment";

  const actionBtn =
    "inline-flex items-center justify-center rounded-xl border border-dulia-line bg-white/90 p-2.5 text-sm text-dulia-navy transition hover:bg-dulia-parchment " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft";

  const actionLink =
    "inline-flex items-center gap-2 rounded-xl border border-dulia-line bg-white/90 px-4 py-2 text-sm font-medium text-dulia-navy transition hover:bg-dulia-parchment " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft";

  const sectionCard =
    "min-w-0 overflow-hidden rounded-[28px] border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft";

  const softPanel =
    "min-w-0 overflow-hidden rounded-[24px] border border-dulia-line bg-dulia-parchment/55 dark:border-dulia-lineStrong/30 dark:bg-dulia-night";

  const quotesList = Array.isArray(saint?.quotes)
    ? saint.quotes.filter(Boolean)
    : [];
  const prayersList = Array.isArray(saint?.prayers)
    ? saint.prayers.filter(Boolean)
    : [];

  const tabItems = [
    { id: "bio", label: "Bio", show: !!String(saint?.bio || "").trim() },
    {
      id: "quotes",
      label: "Quotes",
      show: quotesList.length > 0,
      count: quotesList.length,
    },
    {
      id: "prayers",
      label: "Prayers",
      show: prayersList.length > 0,
      count: prayersList.length,
    },
    {
      id: "gallery",
      label: "Gallery",
      show: galleryImages.length > 0,
      count: galleryImages.length,
    },
  ].filter((tab) => tab.show);

  const currentTab = tabItems.some((tab) => tab.id === activeTab)
    ? activeTab
    : tabItems[0]?.id || "";

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 overflow-x-hidden px-3 pt-2 pb-4 sm:px-4 md:px-6 md:pt-3 md:pb-6 lg:pt-4 lg:pb-8">
      <ImageCarouselModal
        open={viewerOpen}
        images={galleryImages}
        index={viewerIndex}
        onClose={closeViewer}
        onPrev={prevImage}
        onNext={nextImage}
        title={saint?.name || "Images"}
      />

      <div className="flex min-w-0 flex-col gap-3 rounded-[24px] border border-dulia-line bg-dulia-parchment/55 p-3 shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night sm:flex-row sm:items-center sm:justify-between">
        <Link to="/search" className={`${actionLink} w-full sm:w-auto`}>
          ← Search
        </Link>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {isSignedIn ? (
            <button
              type="button"
              onClick={onToggleFavorite}
              disabled={savingFavorite}
              aria-pressed={isFavorited}
              title={isFavorited ? "Remove from favorites" : "Save to favorites"}
              className={`${favoriteChipBase} ${favoriteChipInteractive} ${favoriteChipDisabled}`}
            >
              <FaHeart className="text-[12px]" aria-hidden="true" />
              {favoritesCount}
            </button>
          ) : (
            <span
              className={`${favoriteChipBase} ${favoriteChipInteractive}`}
              title="Sign in to save favorites"
            >
              <FaHeart className="text-[12px]" aria-hidden="true" />
              {favoritesCount}
            </span>
          )}

          {/* <span className={statChip}>
            <FaEye className="text-[12px]" aria-hidden="true" />
            {viewsCount}
          </span> */}

          {shareStatus ? (
            <span className="min-w-0 break-words text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
              {shareStatus}
            </span>
          ) : null}

          {isAdmin && (
            <Link
              to={`/admin/edit/${id}`}
              className={actionBtn}
              title="Edit"
              aria-label="Edit"
            >
              <FaPencilAlt aria-hidden="true" />
            </Link>
          )}

          <button
            type="button"
            onClick={onShare}
            className={actionBtn}
            title="Share"
            aria-label="Share"
          >
            <FaShareAlt aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="grid min-w-0 gap-5 lg:grid-cols-12 lg:items-start">
        <div className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:col-span-4">
          {heroImage && heroOk ? (
            <div className={sectionCard}>
              <div className="overflow-hidden rounded-t-[28px] border-b border-dulia-line dark:border-dulia-lineStrong/30">
                <button
                  type="button"
                  onClick={() => openViewerAt(0)}
                  className="block aspect-[4/3] w-full bg-dulia-parchment/40 dark:bg-dulia-night sm:aspect-square lg:aspect-[4/5]"
                  title="View images"
                >
                  <img
                    src={heroImage}
                    alt={saint.name}
                    className="h-full w-full object-cover object-top cursor-zoom-in"
                    loading="lazy"
                    onError={() => setHeroOk(false)}
                  />
                </button>
              </div>

              {heroCaption ? (
                <div className="px-4 py-3 text-xs text-dulia-navy/75 dark:text-dulia-parchment/75">
                  {heroCaption}
                </div>
              ) : null}
            </div>
          ) : (
            <div className={`${sectionCard} flex aspect-[4/3] items-center justify-center text-sm text-dulia-navy/60 dark:text-dulia-parchment/60 sm:aspect-square lg:aspect-[4/5]`}>
              No hero image
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-5 lg:col-span-8">
          <section className={sectionCard}>
            <div className="border-b border-dulia-line bg-dulia-parchment/60 px-4 py-4 dark:border-dulia-lineStrong/30 dark:bg-dulia-night sm:px-5">
              {stageLabel(saint) ? (
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  {stageLabel(saint)}
                </p>
              ) : null}
              <h1 className="mt-2 break-words text-2xl font-bold leading-tight text-dulia-ink sm:text-4xl dark:text-dulia-parchment">
                {cleanName(saint.name)}
              </h1>
              {(saint.countryLower || saint.country) && (
                <div className="mt-3 text-sm text-dulia-navy/75 dark:text-dulia-parchment/75">
                  <CountryFlag saint={saint} />
                </div>
              )}
            </div>

            <div className="space-y-5 px-4 py-5 sm:px-5">
              {categories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {categories.map((c, i) => (
                    <Chip key={`cat-${i}`}>{c}</Chip>
                  ))}
                </div>
              ) : null}

              {patronage.length > 0 ? (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                    Patronage
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {patronage.map((p, i) => (
                      <Chip key={`pat-${i}`}>{p}</Chip>
                    ))}
                  </div>
                </div>
              ) : null}

              {tags.length > 0 ? (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 12).map((t, i) => (
                      <Chip key={`tag-${i}`}>{t}</Chip>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className={sectionCard}>
            <div className="border-b border-dulia-line bg-dulia-parchment/60 px-5 py-4 dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
              <h2 className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
                Details
              </h2>
            </div>

            <div className="grid min-w-0 gap-x-5 gap-y-1 px-4 py-4 sm:grid-cols-2 sm:px-5">
              <FieldRow label="Born" value={formatDateDMYYY(saint.bornText)} />
              <FieldRow label="Died" value={formatDateDMYYY(saint.diedText)} />
              <FieldRow
                label="Beatified"
                value={formatDateDMYYY(saint.beatifiedText)}
              />
              <FieldRow
                label="Canonized"
                value={formatDateDMYYY(saint.canonizedText)}
              />

              {feastPairs.length === 1 ? (
                <FieldRow
                  label="Feast"
                  value={formatFeastDayMonth(feastPairs[0])}
                />
              ) : feastPairs.length > 1 ? (
                <FieldRow
                  label="Feasts"
                  value={feastPairs.map(formatFeastDayMonth).join(", ")}
                />
              ) : null}
            </div>
          </section>

          {tabItems.length ? (
        <section className={`${sectionCard} flex flex-col`}>
          <div className="shrink-0 border-b border-dulia-line bg-dulia-parchment/60 px-3 py-3 dark:border-dulia-lineStrong/30 dark:bg-dulia-night sm:px-5">
            <div
              className="flex min-w-0 flex-wrap gap-1.5 rounded-[20px] border border-dulia-line bg-white/35 p-1 dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft/60 sm:gap-2"
              role="tablist"
              aria-label="Profile sections"
            >
              {tabItems.map((tab) => {
                const selected = currentTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setActiveTab(tab.id)}
                    className={`min-w-0 flex-1 rounded-2xl px-1.5 py-2 text-xs font-semibold transition sm:flex-none sm:px-3.5 sm:text-sm ${
                      selected
                        ? "bg-dulia-panel text-dulia-ink shadow-soft dark:bg-dulia-night dark:text-dulia-goldSoft"
                        : "text-dulia-navy/70 hover:bg-dulia-panel/70 hover:text-dulia-navy dark:text-dulia-parchment/70 dark:hover:bg-dulia-night dark:hover:text-dulia-parchment"
                    }`}
                  >
                    {tab.label}
                    {tab.count ? (
                      <span className="ml-1.5 text-xs font-medium opacity-70">
                        {tab.count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-[55vh] min-h-[320px] min-w-0 overflow-y-auto px-4 py-4 sm:h-[60vh] sm:px-5 sm:py-5 lg:h-[640px]">
            {currentTab === "bio" ? (
              <p className="whitespace-pre-line break-words text-[15px] leading-7 text-dulia-ink dark:text-dulia-parchment/90">
                {saint.bio}
              </p>
            ) : null}

            {currentTab === "quotes" ? (
              <ul className="grid min-w-0 gap-3 lg:grid-cols-2">
                {quotesList.map((q, i) => {
                  const qq = normalizeOuterDoubleQuotes(q);
                  return (
                    <li key={i} className={softPanel}>
                      <div className="flex h-full min-w-0 flex-col justify-between gap-3 px-4 py-4 sm:flex-row sm:items-start">
                        <div className="min-w-0 whitespace-pre-line break-words text-dulia-ink dark:text-dulia-parchment/90">
                          <span className="block border-l-4 border-dulia-gold pl-3">
                            {qq}
                          </span>
                        </div>
                        <CopyButton text={qq} label="Copy" />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {currentTab === "prayers" ? (
              <ul className="grid min-w-0 gap-3 xl:grid-cols-2">
                {prayersList.map((p, i) => (
                  <li key={i} className={softPanel}>
                    <div className="flex h-full min-w-0 flex-col justify-between gap-3 px-4 py-4 sm:flex-row sm:items-start">
                      <div className="min-w-0 whitespace-pre-line break-words text-dulia-ink dark:text-dulia-parchment/90">
                        {p}
                      </div>
                      <CopyButton text={p} label="Copy" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}

            {currentTab === "gallery" ? (
              <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {galleryImages.map((url, i) => {
                  const caption = String(galleryCaptions?.[i] ?? "").trim();

                  return (
                    <div
                      key={url || i}
                      className="overflow-hidden rounded-[24px] border border-dulia-line bg-dulia-parchment/45 text-left transition hover:-translate-y-0.5 dark:border-dulia-lineStrong/30 dark:bg-dulia-night"
                    >
                      <button
                        type="button"
                        className="block w-full"
                        onClick={() => openViewerAt(i)}
                        title="View images"
                      >
                        <img
                          src={url}
                          alt={`${saint.name} ${i + 1}`}
                          className="h-44 w-full cursor-zoom-in object-cover sm:h-52"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </button>

                      <div className="flex min-w-0 flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 break-words">
                          {caption ? (
                            <div className="text-xs text-dulia-navy/75 dark:text-dulia-parchment/75">
                              {caption}
                            </div>
                          ) : null}
                          <SourceLink url={url} />
                        </div>
                        <button
                          type="button"
                          onClick={() => copyText(url)}
                          className="self-start text-[11px] font-medium text-dulia-navy/75 underline decoration-dulia-lineStrong/50 underline-offset-4 hover:text-dulia-navy dark:text-dulia-parchment/70 dark:decoration-dulia-gold/40 dark:hover:text-dulia-goldSoft sm:shrink-0"
                          title="Copy image URL"
                        >
                          Copy URL
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </section>
          ) : null}
        </div>
      </div>

      {contributors.length ? (
        <p className="px-1 pt-4 text-sm italic leading-7 text-dulia-navy/75 dark:text-dulia-parchment/75">
          Profile created with contributions by {formatContributorList(contributors)}.
        </p>
      ) : null}
    </div>
  );
}
